import { NgZone } from '@angular/core';
import { Intersection, Object3D, Plane, Raycaster, Vector2, Vector3 } from 'three';
import RenderingContext from './rendering-context';
import { MainView } from './main-view';

export class MouseActions {
  public isActive: boolean;
  private canvasElement: HTMLElement;
  private mousePosition = new Vector2();
  private raycaster: Raycaster;
  private plane: Plane;
  private intersectionWithPlane: Vector3;
  private timeout: ReturnType<typeof setTimeout> = null;
  private measureEventReference: EventListener;

  constructor(
    private renderingContext: RenderingContext<any>,
    private zone: NgZone,
    private mainView: MainView,
  ) {
    this.canvasElement = this.renderingContext.renderer.domElement;
    this.raycaster = new Raycaster();
    this.raycaster.params.Line.threshold = 300;
    this.plane = new Plane();
    this.intersectionWithPlane = new Vector3();
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    clearTimeout(this.timeout);
    this.mainView.startAnimation();
  }

  private onMouseUp(event: MouseEvent): void {
    event.preventDefault();
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.mainView.stopAnimation();
      this.timeout = null;
    }, 25);
  }

  private onScroll(event: Event): void {
    event.preventDefault();
    if (this.timeout === null) {
      this.mainView.startAnimation();
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.mainView.stopAnimation();
      this.timeout = null;
    }, 25);
  }

  public activate(): void {
    this.zone.runOutsideAngular(() => {
      this.canvasElement.addEventListener('pointerdown', this.onMouseDown.bind(this));
      this.canvasElement.addEventListener('pointerup', this.onMouseUp.bind(this));
      this.canvasElement.addEventListener('wheel', this.onScroll.bind(this));
    });
    if (!this.isActive) {
      this.zone.runOutsideAngular(() => {
        this.canvasElement.addEventListener(
          'pointermove',
          (this.measureEventReference = this.measureEvent.bind(this) as EventListener),
          false,
        );
      });
      this.isActive = true;
    }
  }

  public deactivate(): void {
    this.zone.runOutsideAngular(() => {
      this.canvasElement.removeEventListener('pointerdown', this.onMouseDown.bind(this));
      this.canvasElement.removeEventListener('pointerup', this.onMouseUp.bind(this));
      this.canvasElement.removeEventListener('wheel', this.onScroll.bind(this));
    });
    if (this.isActive) {
      this.zone.runOutsideAngular(() => {
        this.canvasElement.removeEventListener('pointermove', this.measureEventReference, false);
      });

      this.isActive = false;
    }
  }

  private measureEvent(event: PointerEvent): void {
    event.preventDefault();
    const rect: DOMRect = this.canvasElement.getBoundingClientRect();
    this.mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.renderingContext.camera);
    this.raycaster.ray.intersectPlane(this.plane, this.intersectionWithPlane);
    const measureObject: Object3D = this.renderingContext.scene.getObjectByName('Measure');
    const loadsObjects: Object3D[] =
      this.renderingContext.scene.getObjectByName('loadsGroup').children;
    const intersects: Intersection[] = this.raycaster.intersectObjects(
      measureObject ? [measureObject, ...loadsObjects] : [...loadsObjects],
      true,
    );
    let eventData: MeasureDispatchEvent;
    const allowedIntersects: string[] = ['planeVertical', 'planeHeight', 'planeHorizontal'];
    const intersect: Intersection = [intersects[0], intersects[1]].find((object: Intersection) =>
      allowedIntersects.some((allowed: string) => object?.object?.name.includes(allowed)),
    );
    if (intersect) {
      eventData = {
        intersect: intersect,
        mousePosition: { x: event.offsetX, y: event.offsetY },
      };
      this.canvasElement.style.cursor = 'default';
    } else {
      eventData = {
        intersect: undefined,
        mousePosition: { x: event.offsetX, y: event.offsetY },
      };
      if (this.canvasElement.style.cursor === 'default') {
        this.canvasElement.style.cursor = 'grab';
      }
    }
    this.renderingContext.renderer.domElement.dispatchEvent(
      new CustomEvent('measure', { detail: eventData }),
    );
  }
}

interface MeasureDispatchEvent {
  intersect: Intersection;
  mousePosition: { x: number; y: number };
}
